import type { ApiLanguage } from '@luacentrix/shared';

const LOCALES: Record<ApiLanguage, string> = {
  en: 'en-GB',
  th: 'th-TH',
};

const DURATION_UNITS: Record<ApiLanguage, { day: string; hour: string; minute: string }> = {
  en: { day: 'd', hour: 'h', minute: 'min' },
  th: { day: 'วัน', hour: 'ชม.', minute: 'นาที' },
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const BYTE_STEP = 1024;

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

const dateTime = new Map<string, Intl.DateTimeFormat>();
const decimal = new Map<string, Intl.NumberFormat>();
const fixed = new Map<string, Intl.NumberFormat>();

function dateTimeFormat(language: ApiLanguage): Intl.DateTimeFormat {
  const locale = LOCALES[language];
  const existing = dateTime.get(locale);
  if (existing !== undefined) return existing;
  const created = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  dateTime.set(locale, created);
  return created;
}

function decimalFormat(language: ApiLanguage): Intl.NumberFormat {
  const locale = LOCALES[language];
  const existing = decimal.get(locale);
  if (existing !== undefined) return existing;
  const created = new Intl.NumberFormat(locale);
  decimal.set(locale, created);
  return created;
}

export function formatTimestamp(millis: number, language: ApiLanguage): string {
  if (!Number.isSafeInteger(millis) || millis < 0) return '—';
  return dateTimeFormat(language).format(new Date(millis));
}

export function formatCount(value: number, language: ApiLanguage): string {
  return decimalFormat(language).format(value);
}

export function formatDuration(millis: number, language: ApiLanguage): string {
  if (!Number.isFinite(millis) || millis < 0) return '—';
  const units = DURATION_UNITS[language];
  if (millis < MINUTE) return `${decimalFormat(language).format(Math.round(millis / 1000))} s`;
  const days = Math.floor(millis / DAY);
  const hours = Math.floor((millis % DAY) / HOUR);
  const minutes = Math.floor((millis % HOUR) / MINUTE);
  const parts: Array<string> = [];
  if (days > 0) parts.push(`${decimalFormat(language).format(days)} ${units.day}`);
  if (hours > 0) parts.push(`${decimalFormat(language).format(hours)} ${units.hour}`);
  if (minutes > 0 && days === 0) parts.push(`${decimalFormat(language).format(minutes)} ${units.minute}`);
  if (parts.length === 0) return `${decimalFormat(language).format(1)} ${units.minute}`;
  return parts.join(' ');
}

export function remainingMillis(expiresAt: number | null, reference: number): number | null {
  if (expiresAt === null) return null;
  return Math.max(expiresAt - reference, 0);
}

function fixedFormat(language: ApiLanguage, digits: number): Intl.NumberFormat {
  const key = `${LOCALES[language]}:${digits}`;
  const existing = fixed.get(key);
  if (existing !== undefined) return existing;
  const created = new Intl.NumberFormat(LOCALES[language], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  fixed.set(key, created);
  return created;
}

export function formatBytes(value: number, language: ApiLanguage): string {
  if (!Number.isSafeInteger(value) || value < 0) return '—';
  if (value < BYTE_STEP) return `${fixedFormat(language, 0).format(value)} B`;
  let scaled = value / BYTE_STEP;
  let unit = 1;
  while (scaled >= BYTE_STEP && unit < BYTE_UNITS.length - 1) {
    scaled /= BYTE_STEP;
    unit += 1;
  }
  const digits = scaled < 10 ? 1 : 0;
  return `${fixedFormat(language, digits).format(scaled)} ${BYTE_UNITS[unit] ?? 'B'}`;
}

export function formatExtension(fileName: string): string {
  const separator = fileName.lastIndexOf('.');
  if (separator <= 0 || separator === fileName.length - 1) return '';
  return fileName.slice(separator + 1).toLowerCase();
}
